import { useCallback, useMemo } from 'react';

import { readContract, waitForTransactionReceipt } from '@wagmi/core';
import { Address } from 'viem';
import {
  useReadContract,
  useBalance as useTokenBalance,
} from 'wagmi';

import { useAuth } from '@contexts/common/AuthorizationContext';

import { config } from '@tools/wagmi';

import { useWriteContractExtraGas } from '@utils/contract.utils';
import { applyDecimals, toBN } from '@utils/formaters.utils';

import showToast from '@components/common/Toast';

import {
  CHAIN_ID,
  DEFAULT_CHAIN_ID,
  ZERO_ADDRESS,
} from '@constants/common/chain.constants';
import {
  ErrorMessages,
  SuccessMessages,
} from '@constants/common/messages.constants';
import { ToastType } from '@constants/common/toast.constants';
import { MAX_UINT256 } from '@constants/common/tokens.constants';

import abi from '@abi/common/ERC20Permit';

export const getNonces = (
  address: Address,
  account: Address,
): Promise<bigint> =>
  readContract(config, {
    abi,
    address,
    functionName: 'nonces',
    args: [account],
  });

type ApproveParamType = {
  spender: Address | string;
  tokenAddress: Address | string;
  amount: string;
  decimals: number;
  chainId: CHAIN_ID;
  handleUpdate?: () => Promise<void>;
};

export const useApprove = ({
  spender,
  tokenAddress,
  amount,
  decimals = 18,
  chainId,
  handleUpdate,
}: ApproveParamType) => {
  const { account } = useAuth();

  const {
    data: allowance,
    refetch,
    isPending: isPendingAllowance,
  } = useReadContract({
    abi,
    address: tokenAddress as Address,
    functionName: 'allowance',
    chainId,
    args: [account as Address, spender as Address],
  });

  const { writeContract, isPending: isPendingApprove } =
    useWriteContractExtraGas({
      config,
      mutation: {
        onSuccess: async (data) => {
          const transactionReceipt = await waitForTransactionReceipt(config, {
            hash: data,
          });
          await refetch();
          if (handleUpdate) {
            await handleUpdate();
          }
          showToast(
            {
              title: SuccessMessages.Approved,
              withLink: {
                hash: transactionReceipt.transactionHash,
                chainId: DEFAULT_CHAIN_ID,
              },
            },
            ToastType.Success,
          );
        },
        onError: (e) => {
          console.log('Approve:', e);
          showToast(
            { title: ErrorMessages.DeniedTransaction },
            ToastType.Error,
          );
        },
      },
    });

  const isApprove = useMemo(
    () =>
      tokenAddress === ZERO_ADDRESS ||
      (allowance !== undefined &&
        toBN(allowance).gte(applyDecimals(amount || 0, decimals))) ||
      !account,
    [tokenAddress, allowance, amount, decimals, account],
  );

  const approve = useCallback(() => {
    writeContract({
      abi,
      address: tokenAddress as Address,
      functionName: 'approve',
      args: [spender as Address, MAX_UINT256],
      chainId,
    });
  }, [spender, tokenAddress, writeContract, chainId]);

  return {
    approve,
    isApprove,
    refetch,
    isPendingAllowance,
    isPendingApprove,
  };
};
type BalanceParamType = {
  tokenAddress?: Address;
  chainId?: CHAIN_ID;
  contractAddress?: Address;
};

export const useBalance = ({
  tokenAddress,
  chainId,
  contractAddress,
}: BalanceParamType) => {
  const { account } = useAuth();

  const { data, refetch, isPending } = useTokenBalance({
    config,
    address: contractAddress || account,
    token: tokenAddress === ZERO_ADDRESS ? undefined : tokenAddress,
    chainId,
    query: {
      enabled: !!tokenAddress,
    },
  });

  return {
    balance: toBN(data?.value || 0),
    refetch,
    isPending: isPending && !!account,
  };
};

